import React, { useRef } from "react"
import { Props as focusTrapProps } from "focus-trap-react"

import { Modal } from "@tenforce/toolbox-modal-react"
import { Action, ActionType } from "@tenforce/toolbox-button-push-react"
import { ColorName } from "@tenforce/toolbox-default-style"

import { GlobalState } from "../../../store"
import { Labels } from "../../../util/Translations"
import { useAppDispatch, useAppSelector } from "../../../hooks"

import { setIsVisible, reset } from "../slice"
import Messages from "./Messages"
import SuggestedActions from "./SuggestedActions"
import { ISuggestedAction } from ".."

export interface Props {
    id: string
    fetchSuggestedActions: () => void
    addSuggestedActions: (suggestedActions: ISuggestedAction[]) => void
}

export default function AIModal(props: Props): JSX.Element {
    const { fetchSuggestedActions, addSuggestedActions, id } = props

    const aiModalData = useAppSelector(
        (state: GlobalState) => state.aIModal.modals[id],
    )
    if (!aiModalData) {
        return <></>
    }

    const {
        isLoading,
        isVisible,
        labels: { buttonLabel, buttonIcon },
    } = aiModalData

    const dispatch = useAppDispatch()
    const actionRef = useRef<HTMLButtonElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)

    const { AiAssistance } = Labels

    const onTriggerClick = (): void => {
        // we always reset first, to make sure
        // there is no leftover data that was loaded
        // if the modal was closed before fetching has finished
        dispatch(reset(id))
        // the setVisible(true) is part of the "fetchedSuggestedActions"
        // thus it's outside of this method's control
        fetchSuggestedActions()
    }

    const hideModal = (): void => {
        dispatch(setIsVisible({ id, value: false }))
    }

    const completeButtonLabel = !!buttonIcon
        ? `${buttonIcon} ${buttonLabel}`
        : buttonLabel

    const modalId = "AIModal"
    // we want focus on the modal, but not any of the buttons in it
    // this is how we also avoid in the Lightbox focusing on the delete button
    // without the fallbackFocus the component would throw an error
    const focusTrap: Partial<focusTrapProps> = {
        focusTrapOptions: {
            initialFocus: containerRef.current ?? `#${modalId}`,
            fallbackFocus: `#${modalId}`,
        },
    }

    return (
        <>
            <Action
                actionType={ActionType.button}
                data-ft-id="aiTriggerButton"
                data-ft-aiid={id}
                isLoading={isLoading}
                isTransparent={true}
                onClick={onTriggerClick}
                ref={actionRef}
                textColor={ColorName.Jade}
                title={buttonLabel}
            >
                {completeButtonLabel}
            </Action>
            <Modal
                isShown={isVisible}
                containerProps={{
                    id: modalId,
                    targetRef: actionRef,
                    onClose: hideModal,
                }}
                contentProps={{
                    extraClasses: "tw-w-1/3",
                }}
                focusTrapProps={focusTrap}
            >
                <div
                    className="bg-white theme-bubble animate-ai-border"
                    ref={containerRef}
                    tabIndex={-1}
                >
                    <header className="step-header">
                        <h3 className="tw-my-2 tw-mx-3">{AiAssistance}</h3>
                    </header>
                    <div className="tw-flex tw-flex-col tw-px-4 tw-pb-4">
                        <SuggestedActions
                            addSuggestedActions={addSuggestedActions}
                            id={id}
                        />
                        <Messages id={id} />
                    </div>
                </div>
            </Modal>
        </>
    )
}
